import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import Stack from "@mui/system/Stack";
import { useEffect, useState } from "react";
import { ResponsiveRow } from "../../../../constants/layout.constants";
import ModalContainerModule from "../../../modules/modalContainer";
import {
  isEmptyObject,
  isEmptyString,
  isNullUndefined,
} from "../../../../util/util";
import Permit from "../../../../util/permit"
import { getUnitTypes, saveUnit } from "../../../../services/property.service";
import {
  issueResponseMessage,
  issueWarnMessage,
} from "../../../../actions/message";
import { getFacilityCourts } from "../../../../services/courts.service";
import InputCharactersCountWrapper from "../../../inputs/inputCharactersCountWrapper";

/**
 *
 * REQUIRED PROPS
 *
 * handleSaveChanges
 * setEditUnitModal
 * editUnitModal
 * unit
 *
 */

export default function UnitModal(props) {
  const [loading, setLoading] = useState(true);
  const [unitPropertyId, setUnitPropertyId] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("");
  const [sqft, setSqft] = useState(0);
  const [rentPerSqft, setRentPerSqft] = useState(0);
  const [rent, setRent] = useState(0);
  const [beds, setBeds] = useState(0);
  const [baths, setBaths] = useState(0);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [useSquareFootageBasedRent, setUseSquareFootageBasedRent] = useState(false);

  useEffect(() => {
    const promises = [];
    promises.push(
      getUnitTypes()
        .then((unitTypeOptions) => setUnitTypeOptions(unitTypeOptions))
    )
    promises.push(
      getFacilityCourts()
        .then((response) => {
          setCourts(response)
        })
    )
    Promise.all(promises).then(() => setLoading(false))
  }, []);

  useEffect(() => {
    if (!isEmptyObject(props.unit)) {
      setUnitPropertyId(props.unit?.unitPropertyId);
      setName(props.unit?.name);
      setDescription(props.unit?.description);
      setType(props.unit?.type?.id);
      setSqft(props.unit?.sqft);
      setRentPerSqft(props.unit?.rentPerSqft);
      setRent(props.unit?.rent);
      setBeds(props.unit?.beds);
      setBaths(props.unit?.baths);
      setSelectedCourt(props.unit?.courtId);
      // Initialize toggle: check if rent is null/empty/0 AND sqft and rentPerSqft exist
      // Backend converts null rent to "0" string, so we need to check both number and string
      const rentValue = props.unit?.rent;
      const rentNum = Number(rentValue) || 0;
      const sqftValue = Number(props.unit?.sqft) || 0;
      const rentPerSqftValue = Number(props.unit?.rentPerSqft) || 0;
      
      // If rent has a non-zero value, it's standard rent
      // Otherwise, if sqft and rentPerSqft are set, it's square meters based
      const isSqFtBased = 
        (isNullUndefined(rentValue) || 
         rentValue === "" ||
         rentNum === 0) &&
        sqftValue > 0 &&
        rentPerSqftValue > 0;
      setUseSquareFootageBasedRent(isSqFtBased);
    } else {
      // Reset all fields when creating a new unit
      setUnitPropertyId(null);
      setName("");
      setDescription("");
      setType("");
      setSqft(0);
      setRentPerSqft(0);
      setRent(0);
      setBeds(0);
      setBaths(0);
      setSelectedCourt(null);
      setUseSquareFootageBasedRent(false);
    }
    setLoading(false);
  }, [props.unit]);

  const handleToggleSquareFootageBasedRent = (checked) => {
    setUseSquareFootageBasedRent(checked);
    if (checked) {
      // Switching to square meters mode: clear standard rent
      setRent(0);
    } else {
      // Switching to standard rent mode: clear square meters fields
      setSqft(0);
      setRentPerSqft(0);
    }
  };

  const submit = () => {
    if (isEmptyString(name)) {
      issueWarnMessage("The unit must have a name!!");
      return;
    }
    if (isEmptyString(type)) {
      issueWarnMessage("Unit must have an type!!");
      return;
    }
    if (useSquareFootageBasedRent) {
      const sqftNum = Number(sqft) || 0;
      const rentPerSqftNum = Number(rentPerSqft) || 0;
      if (sqftNum <= 0 || isNaN(sqftNum)) {
        issueWarnMessage("Square Meters must be greater than zero when using square meters based rent!!");
        return;
      }
      if (rentPerSqftNum <= 0 || isNaN(rentPerSqftNum)) {
        issueWarnMessage("Rent per Square Meters must be greater than zero when using square meters based rent!!");
        return;
      }
    } else {
      const rentNum = Number(rent) || 0;
      if (rentNum <= 0 || isNaN(rentNum)) {
        issueWarnMessage("Standard Rent must be greater than zero!!");
        return;
      }
    }
    const sendData = {
      id: props.unit?.id ? props.unit?.id : null,
      name: name,
      unitPropertyId: unitPropertyId,
      description: description,
      type: type,
      sqft: sqft,
      rentPerSqft: rentPerSqft,
      rent: useSquareFootageBasedRent ? null : rent,
      beds: beds,
      baths: baths,
      court: selectedCourt
    };
    saveUnit(sendData).then((response) => {
      issueResponseMessage(response);
      props.setUnitModal(false);
    });
  };

  return (
    <ModalContainerModule
      size="specialEditPropertyUnit"
      accept={submit}
      cancel={() => props.setUnitModal(false)}
      openModal={props.unitModal}
      acceptDisabled={loading}
      acceptDisabled2={loading}
      modalTitle={
        isNullUndefined(props.unit?.id)
          ? "Add Unit"
          : `Edit Unit: ${props.unit?.name}`
      }
      acceptButtonText={isNullUndefined(props.unit?.id) ? "Save" : "Update"}
    >
      <ResponsiveRow
        sx={{ width: "100%", height: "auto", alignItems: "center" }}
      >
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          required={true}
        >
          <InputLabel>Name</InputLabel>
          <OutlinedInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ width: { xs: "100%", lg: "90%" } }}
            required={true}
          />
        </FormControl>
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
        >
          <InputLabel>Description</InputLabel>
          <InputCharactersCountWrapper value={description ?? ""} maxLength={180} width={{ xs: "100%", md: "90%" }}>
            <OutlinedInput
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ width: "100%" }}
            />
          </InputCharactersCountWrapper>
        </FormControl>
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
        >
          <InputLabel>Type *</InputLabel>
          <Select
            label="Type *"
            sx={{ width: { xs: "100%", md: "90%" } }}
            id={"type"}
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            {unitTypeOptions && unitTypeOptions.length > 0 ? (
              unitTypeOptions.map(function (unitType, i) {
                return (
                  <MenuItem key={i} value={unitType.value}>
                    {unitType.label}
                  </MenuItem>
                );
              }, this)
            ) : (
              <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Rent Based on Square Meters</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useSquareFootageBasedRent}
                  onChange={(e) => handleToggleSquareFootageBasedRent(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Stack>
        </FormControl>
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          required={useSquareFootageBasedRent}
        >
          <InputLabel>Square Meters</InputLabel>
          <OutlinedInput
            label="Square Meters"
            type="number"
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            sx={{ width: { xs: "100%", lg: "90%" } }}
            inputProps={{ min: 0 }}
            required={useSquareFootageBasedRent}
          />
        </FormControl>
        {!useSquareFootageBasedRent && (
          <FormControl
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
            required={true}
          >
            <InputLabel>Standard Rent</InputLabel>
            <OutlinedInput
              label="Standard Rent"
              type="number"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
              sx={{ width: { xs: "100%", lg: "90%" } }}
              inputProps={{ min: 0 }}
              required={true}
            />
          </FormControl>
        )}
        {useSquareFootageBasedRent && (
          <FormControl
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
            required={true}
          >
            <InputLabel>Rent per Sq M</InputLabel>
            <OutlinedInput
              label="Rent per Sq M"
              type="number"
              value={rentPerSqft}
              onChange={(e) => setRentPerSqft(e.target.value)}
              sx={{ width: { xs: "100%", lg: "90%" } }}
              inputProps={{ min: 0 }}
              required={true}
            />
          </FormControl>
        )}
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
        >
          <InputLabel>Beds</InputLabel>
          <OutlinedInput
            label="Beds"
            type="number"
            value={beds}
            onChange={(e) => setBeds(e.target.value)}
            sx={{ width: { xs: "100%", lg: "90%" } }}
            inputProps={{ min: 0 }}
          />
        </FormControl>
        <FormControl
          sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
        >
          <InputLabel>Baths</InputLabel>
          <OutlinedInput
            label="Baths"
            type="number"
            value={baths}
            onChange={(e) => setBaths(e.target.value)}
            sx={{ width: { xs: "100%", lg: "90%" } }}
            inputProps={{ min: 0 }}
          />
        </FormControl>
        <Permit services="HOA">
          <FormControl
            sx={{ width: { xs: "100%", lg: "33.3%" }, marginBottom: "10px" }}
          >
            <InputLabel>Court</InputLabel>
            <Select
              sx={{ width: "90%" }}
              id={"court"}
              name={"court"}
              label={"Court"}
              isrequired={true}
              value={selectedCourt}
              onChange={(event) => setSelectedCourt(event.target.value)}
            >
              {courts && courts.length > 0 ? (
                courts.map(function (court, i) {
                  return (
                    <MenuItem key={i} value={court?.courtId}>
                      {court.name}
                    </MenuItem>
                  );
                }, this)
              ) : (
                <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
              )}
            </Select>
          </FormControl>
        </Permit>
      </ResponsiveRow>
    </ModalContainerModule>
  );
}
